import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { Space } from "@stenajs-webui/core";
import {
  FlatButton,
  PrimaryButton,
  ResultListBanner,
  SecondaryButton,
  stenaUserSingle,
} from "@stenajs-webui/elements";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageHeader } from "@/components/page-header/PageHeader";
import { StoreState } from "@/redux/RootReducer";
import { adminTargetActions } from "./components/all-routes/redux/actions";
import { adminTargetSelectors } from "./components/all-routes/redux/selectors";
import {
  UpdateAllRoutesAdminData,
  updateAllRoutesAdminTarget,
} from "./components/all-routes/redux/thunks/AdminTargetMutations";

const selector = (state: StoreState) => state.adminTarget.allRoutesData;

export const AdminTargetHeader: React.FC = () => {
  const allRoutesData = useSelector(selector);
  const dispatch = useDispatch();
  const { bannerState } = useSelector(adminTargetSelectors.getProgress);

  const handleResetTable = () => {
    const persistedData = allRoutesData.map((data) => {
      return {
        id: data.id,
        editable: data.persisted,
        persisted: data.persisted,
      };
    });

    dispatch(adminTargetActions.resetTableState(persistedData));
  };

  const isDataModified = useMemo(() => {
    return allRoutesData.some((ard) => {
      return JSON.stringify(ard.editable) !== JSON.stringify(ard.persisted);
    });
  }, [allRoutesData]);

  const handleUpdateAdminRouteTargets = async (): Promise<void> => {
    const dataForUpdate = allRoutesData
      .filter((ard) => {
        return JSON.stringify(ard.persisted) !== JSON.stringify(ard.editable);
      })
      .map((data) => {
        const {
          editable: {
            year,
            routePairCode,
            offPeakAcc,
            offPeakUnAcc,
            peakAcc,
            peakUnAcc,
            shoulderUnAcc,
            shoulderAcc,
            volumeTarget,
          },
        } = data;

        return {
          year,
          routePairCode,
          offPeakAcc,
          offPeakUnAcc,
          peakAcc,
          peakUnAcc,
          shoulderUnAcc,
          shoulderAcc,
          volume: volumeTarget,
        };
      });

    dispatch(
      updateAllRoutesAdminTarget(dataForUpdate as UpdateAllRoutesAdminData)
    );

    const newTableData = allRoutesData.map((ard) => {
      return {
        id: ard.id,
        editable: ard.editable,
        persisted: ard.editable,
      };
    });

    dispatch(adminTargetActions.setRowsAdminTargetAllRoutes(newTableData));
  };

  const headerContentRight = (): React.ReactNode => {
    return (
      <>
        <SecondaryButton
          disabled={!isDataModified}
          label={"Discard"}
          onClick={() => handleResetTable()}
          data-testid={testIdConstants.groupFreightTargetsDiscardButton}
        />
        <Space num={2} />
        <PrimaryButton
          disabled={!isDataModified}
          label={"Save"}
          onClick={() => handleUpdateAdminRouteTargets()}
          data-testid={testIdConstants.groupFreightTargetsSaveButton}
        />
      </>
    );
  };

  return (
    <PageHeader
      title={"Group freight targets"}
      sticky
      width={"100vw"}
      offsetLeft={0}
      offsetTop={0}
      icon={stenaUserSingle}
      contentRight={headerContentRight()}
      contentUnder={
        bannerState && (
          <ResultListBanner
            bannerState={bannerState}
            variant={"error"}
            contentRight={
              <FlatButton
                label={"Close"}
                onClick={() =>
                  dispatch(
                    adminTargetActions.setProgress({
                      bannerState: undefined,
                    })
                  )
                }
              />
            }
          />
        )
      }
    />
  );
};
